import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  InputLabel,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowForward,
  WarningAmber,
  Close,
  InfoOutlined,
} from '@mui/icons-material';
import {
  LandingClient,
  Config,
  CreateWidgetApiKeyRequest,
} from '../types.generated';
import { useRecaptcha } from '../hooks/useRecaptcha';
import useTexts from '../hooks/useTexts';

interface FieldErrors {
  customerId?: string;
  orgId?: string;
  email?: string;
}

function CreateApiKeyForm() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [orgId, setOrgId] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [success, setSuccess] = useState<boolean>(false);
  const [emailTooltipOpen, setEmailTooltipOpen] = useState(false);
  const [customerIdTooltipOpen, setCustomerIdTooltipOpen] = useState(false);

  const { getToken } = useRecaptcha();
  const texts = useTexts();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSuccess(false);

    const errors: FieldErrors = {};

    if (!orgId.trim()) {
      errors.orgId = texts.ERR_ORG_ID_REQUIRED;
    }

    if (!email.trim()) {
      errors.email = texts.ERR_EMAIL_REQUIRED;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const orgIdTrimmed = orgId.trim();
    if (!/^\d+$/.test(orgIdTrimmed) || orgIdTrimmed.length > 20) {
      setFieldErrors({ orgId: texts.ERR_ORG_ID_INVALID });
      return;
    }

    let customerIdNum: number | undefined;
    if (customerId.trim()) {
      customerIdNum = parseInt(customerId, 10);
      if (isNaN(customerIdNum) || customerIdNum <= 0) {
        setFieldErrors({ customerId: texts.ERR_CUSTOMER_ID_INVALID });
        return;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFieldErrors({ email: texts.ERR_EMAIL_INVALID });
      return;
    }

    setIsLoading(true);

    try {
      const recaptchaToken = await getToken('CREATE_API_KEY');
      if (!recaptchaToken) {
        setError(texts.ERR_RECAPTCHA);
        setIsLoading(false);
        return;
      }

      const config = new Config(undefined, recaptchaToken);
      const client = new LandingClient(config);

      const request = {
        orgId: orgIdTrimmed,
        customerId: customerIdNum,
        email: email.trim(),
      } as CreateWidgetApiKeyRequest;

      await client.apiKeyRequest(request);
      setSuccess(true);
    } catch (err: unknown) {
      console.error('Error creating API Key:', err);
      if (
        err &&
        typeof err === 'object' &&
        'status' in err &&
        err.status === 400
      ) {
        if (customerIdNum) {
          setError(texts.ERR_ORG_CUSTOMER_MISMATCH);
        } else {
          setError(texts.ERR_ORG_EMAIL_MISMATCH);
        }
      } else {
        setError(texts.ERR_REQUEST_FAILED);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      const recaptchaToken = await getToken('CREATE_API_KEY');
      if (!recaptchaToken) return;

      const config = new Config(undefined, recaptchaToken);
      const client = new LandingClient(config);

      let customerIdNum: number | undefined;
      if (customerId.trim()) {
        customerIdNum = parseInt(customerId, 10);
      }

      const request = {
        orgId: orgId.trim(),
        customerId: customerIdNum,
        email: email.trim(),
      } as CreateWidgetApiKeyRequest;

      await client.apiKeyRequest(request);
    } catch (err: unknown) {
      console.error('Error resending email:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          mx: 'auto',
          my: { sm: 0, md: 4 },
        }}
      >
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderLeft: '6px solid #95C620',
            p: 3,
            float: 'left',
            mt: { sm: 0, md: 6 },
            mr: { sm: 0, md: 3 },
            width: { sm: '100%', md: 640 },
            mb: 3,
          }}
        >
          <Box mb={3}>
            <Typography
              variant="body1"
              component="span"
              sx={{ fontWeight: 700 }}
            >
              {texts.SUCCESS_REQUEST_SENT}
            </Typography>
            <br />
            <Typography variant="body1" component="span">
              {texts.SUCCESS_API_KEY_SENT}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            component="span"
            color="primary.main"
            onClick={isLoading ? undefined : handleResend}
            sx={{
              fontWeight: 'bold',
              textDecoration: 'underline',
              cursor: isLoading ? 'default' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              fontSize: 16,
            }}
          >
            {isLoading ? texts.SUBMITTING : texts.RESEND_EMAIL}
          </Typography>
        </Box>
        <Box textAlign={{ xs: 'center', md: 'unset' }}>
          <img src="/img/worker.png" alt="PPL" width={350} height={350} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 2, p: { xs: 0, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1, lineHeight: 1.2 }}
        >
          {texts.CREATE_API_KEY_TITLE}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          {texts.CREATE_API_KEY_SUBTITLE}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            pl: 3,
            pr: 2,
            py: 2,
            backgroundColor: '#ffffff',
            borderLeft: '6px solid',
            borderColor: 'error.main',
            mb: 4,
          }}
        >
          <WarningAmber
            sx={{ color: 'error.main', mt: '2px', flexShrink: 0 }}
          />
          <Typography variant="body1" sx={{ color: '#1D1D1D' }}>
            {error}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setError('')}
            sx={{ flexShrink: 0, mt: '-4px' }}
          >
            <Close />
          </IconButton>
        </Box>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} style={{ maxWidth: 444 }}>
        {/* Field: IČO */}
        <Box sx={{ mb: 3 }}>
          <InputLabel shrink={false} required sx={{ mb: 1 }}>
            {texts.ORG_ID_LABEL}
          </InputLabel>
          <TextField
            fullWidth
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            disabled={isLoading}
            // placeholder={texts.ORG_ID_PLACEHOLDER}
            inputProps={{ maxLength: 20 }}
            error={!!fieldErrors.orgId}
            helperText={fieldErrors.orgId}
          />
        </Box>

        {/* Field: Email */}
        <Box sx={{ mb: 3 }} data-tooltip-anchor>
          <InputLabel
            shrink={false}
            sx={{
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {texts.EMAIL_LABEL}
            <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
              *
            </Typography>
            <Tooltip
              title={texts.EMAIL_TOOLTIP}
              arrow
              placement="top-start"
              {...(!isDesktop
                ? {
                    open: emailTooltipOpen,
                    disableHoverListener: true,
                    disableFocusListener: true,
                    disableTouchListener: true,
                  }
                : {})}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'alignToInput',
                      enabled: true,
                      phase: 'afterMain' as const,
                      fn({ state }: any) {
                        const ref = state.elements.reference;
                        const box = ref.closest('[data-tooltip-anchor]');
                        if (box && state.modifiersData.popperOffsets) {
                          const boxRect = box.getBoundingClientRect();
                          const refRect = ref.getBoundingClientRect();
                          const shift = refRect.left - boxRect.left;
                          state.modifiersData.popperOffsets.x -= shift;
                          if (state.modifiersData.arrow) {
                            const arrowEl = state.elements.arrow;
                            const arrowWidth = arrowEl
                              ? arrowEl.offsetWidth
                              : 0;
                            state.modifiersData.arrow.x =
                              shift + refRect.width / 2 - arrowWidth / 2;
                          }
                        }
                      },
                    },
                    {
                      name: 'offset',
                      options: {
                        offset: [0, isDesktop ? 8 : 4],
                      },
                    },
                  ],
                },
                tooltip: {
                  sx: {
                    bgcolor: '#004B93',
                    color: '#fff',
                    fontWeight: 400,
                    fontSize: '18px',
                    lineHeight: '25px',
                    minWidth: 444,
                    p: '20px 16px',
                    borderRadius: 0,
                  },
                },
                arrow: {
                  sx: {
                    color: '#004B93',
                    fontSize: 20,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  if (!isDesktop) setEmailTooltipOpen((prev) => !prev);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  color: 'primary.main',
                }}
              >
                <InfoOutlined sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </InputLabel>
          <TextField
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            // placeholder={texts.EMAIL_PLACEHOLDER}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
          />
        </Box>

        {/* Field: Cust ID */}
        <Box sx={{ mb: 3 }} data-tooltip-anchor>
          <InputLabel
            shrink={false}
            sx={{
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {texts.CUSTOMER_ID_LABEL}
            <Tooltip
              title={texts.CUSTOMER_ID_TOOLTIP}
              arrow
              placement="top-start"
              {...(!isDesktop
                ? {
                    open: customerIdTooltipOpen,
                    disableHoverListener: true,
                    disableFocusListener: true,
                    disableTouchListener: true,
                  }
                : {})}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'alignToInput',
                      enabled: true,
                      phase: 'afterMain' as const,
                      fn({ state }: any) {
                        const ref = state.elements.reference;
                        const box = ref.closest('[data-tooltip-anchor]');
                        if (box && state.modifiersData.popperOffsets) {
                          const boxRect = box.getBoundingClientRect();
                          const refRect = ref.getBoundingClientRect();
                          const shift = refRect.left - boxRect.left;
                          state.modifiersData.popperOffsets.x -= shift;
                          if (state.modifiersData.arrow) {
                            const arrowEl = state.elements.arrow;
                            const arrowWidth = arrowEl
                              ? arrowEl.offsetWidth
                              : 0;
                            state.modifiersData.arrow.x =
                              shift + refRect.width / 2 - arrowWidth / 2;
                          }
                        }
                      },
                    },
                    {
                      name: 'offset',
                      options: {
                        offset: [0, isDesktop ? 8 : 4],
                      },
                    },
                  ],
                },
                tooltip: {
                  sx: {
                    bgcolor: '#004B93',
                    color: '#fff',
                    fontWeight: 400,
                    fontSize: '18px',
                    lineHeight: '25px',
                    minWidth: 444,
                    p: '20px 16px',
                    borderRadius: 0,
                  },
                },
                arrow: {
                  sx: {
                    color: '#004B93',
                    fontSize: 20,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  if (!isDesktop) setCustomerIdTooltipOpen((prev) => !prev);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  color: 'primary.main',
                  mt: 0,
                }}
              >
                <InfoOutlined sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </InputLabel>
          <TextField
            fullWidth
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            disabled={isLoading}
            // placeholder={texts.CUSTOMER_ID_PLACEHOLDER}
            error={!!fieldErrors.customerId}
            helperText={fieldErrors.customerId}
          />
        </Box>

        {/* Submit Button */}
        <Box sx={{ pt: 4 }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.main', boxShadow: 'none' },
              py: 3,
              px: 3.5,
              justifyContent: 'space-between',
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ mr: 1, color: 'common.white' }}
                />{' '}
                {texts.SUBMITTING}
              </>
            ) : (
              <>
                <span>{texts.CREATE_API_KEY}</span>
                <ArrowForward />
              </>
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
}

export default CreateApiKeyForm;
